import { safetyInsightRows, SAFETY_INSIGHTS_NOTE } from './safetyInsights';
import jsPDF from 'jspdf';

const COLORS = {
  navy: [15, 32, 55],
  navySoft: [28, 51, 78],
  teal: [13, 148, 136],
  blue: [37, 99, 235],
  green: [22, 163, 74],
  amber: [217, 119, 6],
  red: [220, 38, 38],
  ink: [24, 37, 54],
  muted: [91, 107, 126],
  line: [218, 226, 235],
  panel: [246, 249, 252],
  white: [255, 255, 255],
};

const PAGE = { width: 612, height: 792, margin: 42, footerY: 758 };
const EVENT_LABELS = {
  acceleration: 'Rapid acceleration',
  braking: 'Harsh braking',
  cornering: 'Hard cornering',
  speeding: 'Speeding',
};

const safeNumber = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0);
const integer = (value) => Math.round(safeNumber(value)).toLocaleString('en-US');
const decimal = (value, digits = 1) => safeNumber(value).toLocaleString('en-US', {
  minimumFractionDigits: digits,
  maximumFractionDigits: digits,
});
const percent = (value) => `${Math.round(safeNumber(value) * 100)}%`;
const score = (value) => (value == null ? 'NQ' : decimal(value, 1));

function ascii(value, fallback = '-') {
  const text = String(value ?? '').trim();
  if (!text) return fallback;
  return text.normalize('NFKD').replace(/[^\x20-\x7E]/g, '').replace(/\s+/g, ' ').trim() || fallback;
}

function monthLabel(value) {
  const date = new Date(`${value}-01T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return ascii(value);
  return new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' }).format(date);
}

function generatedLabel(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Generation time unavailable';
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
  }).format(date);
}

function setText(doc, color = COLORS.ink, size = 9, style = 'normal') {
  doc.setTextColor(...color);
  doc.setFont('helvetica', style);
  doc.setFontSize(size);
}

function fitText(doc, value, width) {
  const text = ascii(value);
  if (doc.getTextWidth(text) <= width) return text;
  let trimmed = text;
  while (trimmed.length > 1 && doc.getTextWidth(`${trimmed}...`) > width) trimmed = trimmed.slice(0, -1);
  return `${trimmed}...`;
}

function riskTone(value) {
  if (value == null) return { label: 'Not qualified', color: COLORS.muted };
  if (value >= 90) return { label: 'Excellent', color: COLORS.green };
  if (value >= 80) return { label: 'Good', color: COLORS.blue };
  if (value >= 70) return { label: '70-79', color: COLORS.amber };
  return { label: 'Below 70', color: COLORS.blue };
}

function reportReference(report) {
  const company = ascii(report.company?.name, 'fleet').replace(/[^A-Za-z0-9]/g, '').slice(0, 8).toUpperCase() || 'FLEET';
  const generated = String(report.generatedAt || '').slice(0, 10).replaceAll('-', '');
  return `TXO-SAFE-${company}-${generated || 'REPORT'}`;
}

function drawBrandHeader(doc, report) {
  const company = report.company || {};
  const contact = [company.phone, company.email, company.website].filter(Boolean).map((value) => ascii(value)).join('  |  ');
  doc.setFillColor(...COLORS.navy);
  doc.rect(0, 0, PAGE.width, 132, 'F');
  doc.setFillColor(...COLORS.teal);
  doc.rect(0, 0, 9, 132, 'F');
  setText(doc, COLORS.white, 10, 'bold');
  doc.text('TAXIOPS  /  FLEET SAFETY INTELLIGENCE', PAGE.margin, 30);
  setText(doc, COLORS.white, 25, 'bold');
  doc.text('Fleet Safety Summary', PAGE.margin, 62);
  setText(doc, [226, 232, 240], 9.5, 'bold');
  doc.text(fitText(doc, company.name || 'TaxiOps Fleet', 320), PAGE.margin, 82);
  setText(doc, [190, 211, 230], 7.5, 'normal');
  doc.text(fitText(doc, company.address || 'Company address not configured', 320), PAGE.margin, 99);
  doc.text(fitText(doc, contact || 'Company contact details not configured', 320), PAGE.margin, 114);
  doc.text('Prepared for insurance review', PAGE.width - PAGE.margin, 30, { align: 'right' });
  doc.text(generatedLabel(report.generatedAt), PAGE.width - PAGE.margin, 46, { align: 'right' });
  setText(doc, [153, 246, 228], 7, 'bold');
  doc.text('REPORTING PERIOD', PAGE.width - PAGE.margin, 70, { align: 'right' });
  setText(doc, COLORS.white, 8.5, 'bold');
  doc.text(`${monthLabel(report.period?.from)} - ${monthLabel(report.period?.to)}  |  ${report.period?.monthCount || 0} months`, PAGE.width - PAGE.margin, 86, { align: 'right' });
  setText(doc, [190, 211, 230], 7, 'normal');
  doc.text(`Report reference: ${reportReference(report)}`, PAGE.width - PAGE.margin, 103, { align: 'right' });
}

function drawMetric(doc, x, y, width, label, value, note, accent) {
  doc.setFillColor(...COLORS.white);
  doc.setDrawColor(...COLORS.line);
  doc.roundedRect(x, y, width, 68, 7, 7, 'FD');
  doc.setFillColor(...accent);
  doc.roundedRect(x, y, 5, 68, 3, 3, 'F');
  setText(doc, COLORS.muted, 7.5, 'bold');
  doc.text(ascii(label).toUpperCase(), x + 14, y + 17);
  setText(doc, COLORS.ink, 20, 'bold');
  doc.text(ascii(value), x + 14, y + 42);
  setText(doc, COLORS.muted, 7.2, 'normal');
  doc.text(fitText(doc, note, width - 28), x + 14, y + 57);
}

function assessmentText(summary) {
  if (!summary || safeNumber(summary.validMiles) === 0) {
    return 'No valid mileage was recorded for this period. Review data availability before using this report for underwriting.';
  }
  const parts = [
    `The fleet recorded ${decimal(summary.validMiles, 1)} valid miles, with ${percent(summary.scoredCoverage)} speed-limit coverage.`,
    summary.fleetScore == null
      ? 'A qualified fleet score is not yet available.'
      : `The mileage-weighted qualified safety score was ${score(summary.fleetScore)} / 100.`,
  ];
  if (summary.acknowledgements?.rate != null) {
    parts.push(`${percent(summary.acknowledgements.rate)} of delivered safety notices were acknowledged.`);
  }
  parts.push('Monthly trends and supporting driver results provide context for renewal review and coaching.');
  return parts.join(' ');
}

function drawAssessment(doc, report, x, y, width) {
  const tone = riskTone(report.summary?.fleetScore);
  doc.setFillColor(...COLORS.panel);
  doc.setDrawColor(...COLORS.line);
  doc.roundedRect(x, y, width, 72, 8, 8, 'FD');
  doc.setFillColor(...tone.color);
  doc.circle(x + 20, y + 21, 5, 'F');
  setText(doc, COLORS.ink, 10, 'bold');
  doc.text('Fleet performance overview', x + 32, y + 24);
  setText(doc, COLORS.muted, 8.2, 'normal');
  const lines = doc.splitTextToSize(assessmentText(report.summary), width - 28).slice(0, 4);
  doc.text(lines, x + 14, y + 42, { lineHeightFactor: 1.3 });
}

function drawTrendChart(doc, report, x, y, width, height) {
  const months = report.monthly || [];
  doc.setFillColor(...COLORS.white);
  doc.setDrawColor(...COLORS.line);
  doc.roundedRect(x, y, width, height, 8, 8, 'FD');
  setText(doc, COLORS.ink, 10, 'bold');
  doc.text('Fleet safety score trend', x + 14, y + 20);
  setText(doc, COLORS.muted, 7.5, 'normal');
  doc.text('Mileage-weighted qualified score by month', x + 14, y + 34);
  const plot = { x: x + 34, y: y + 49, width: width - 52, height: height - 76 };
  [60, 70, 80, 90, 100].forEach((tick) => {
    const tickY = plot.y + plot.height - ((tick - 60) / 40) * plot.height;
    doc.setDrawColor(...COLORS.line);
    doc.setLineWidth(0.5);
    doc.line(plot.x, tickY, plot.x + plot.width, tickY);
    setText(doc, COLORS.muted, 6.5, 'normal');
    doc.text(String(tick), plot.x - 7, tickY + 2, { align: 'right' });
  });
  const points = months.map((month, index) => {
    if (month.fleetScore == null) return null;
    const pointX = months.length <= 1 ? plot.x + (plot.width / 2) : plot.x + (index / (months.length - 1)) * plot.width;
    const clamped = Math.max(60, Math.min(100, safeNumber(month.fleetScore)));
    const pointY = plot.y + plot.height - ((clamped - 60) / 40) * plot.height;
    return { x: pointX, y: pointY };
  });
  doc.setDrawColor(...COLORS.blue);
  doc.setLineWidth(1.6);
  let previous = null;
  points.forEach((point) => {
    if (point && previous) doc.line(previous.x, previous.y, point.x, point.y);
    previous = point;
  });
  points.forEach((point) => {
    if (!point) return;
    doc.setFillColor(...COLORS.white);
    doc.setDrawColor(...COLORS.blue);
    doc.circle(point.x, point.y, 2.3, 'FD');
  });
  if (months.length) {
    setText(doc, COLORS.muted, 6.8, 'normal');
    doc.text(monthLabel(months[0].month), plot.x, y + height - 11);
    if (months.length > 2) doc.text(monthLabel(months[Math.floor(months.length / 2)].month), plot.x + plot.width / 2, y + height - 11, { align: 'center' });
    doc.text(monthLabel(months[months.length - 1].month), plot.x + plot.width, y + height - 11, { align: 'right' });
  }
}

function drawEventMix(doc, report, x, y, width, height) {
  const mix = report.summary?.eventMix || {};
  const max = Math.max(1, ...Object.values(mix).map(safeNumber));
  doc.setFillColor(...COLORS.white);
  doc.setDrawColor(...COLORS.line);
  doc.roundedRect(x, y, width, height, 8, 8, 'FD');
  setText(doc, COLORS.ink, 10, 'bold');
  doc.text('Road-event mix', x + 14, y + 20);
  Object.keys(EVENT_LABELS).forEach((type, index) => {
    const rowY = y + 44 + index * 27;
    const count = safeNumber(mix[type]);
    setText(doc, COLORS.muted, 7.2, 'normal');
    doc.text(EVENT_LABELS[type], x + 14, rowY);
    doc.setFillColor(...COLORS.line);
    doc.roundedRect(x + 105, rowY - 7, width - 146, 8, 4, 4, 'F');
    doc.setFillColor(...(type === 'braking' ? COLORS.red : type === 'speeding' ? COLORS.amber : COLORS.blue));
    doc.roundedRect(x + 105, rowY - 7, Math.max(count ? 3 : 0, ((width - 146) * count) / max), 8, 4, 4, 'F');
    setText(doc, COLORS.ink, 7.5, 'bold');
    doc.text(integer(count), x + width - 14, rowY, { align: 'right' });
  });
}

function drawCompliance(doc, report, x, y, width, height) {
  const ack = report.summary?.acknowledgements || {};
  doc.setFillColor(...COLORS.navySoft);
  doc.roundedRect(x, y, width, height, 8, 8, 'F');
  setText(doc, COLORS.white, 10, 'bold');
  doc.text('Driver safety engagement', x + 14, y + 20);
  setText(doc, [153, 246, 228], 24, 'bold');
  doc.text(ack.rate == null ? 'N/A' : percent(ack.rate), x + 14, y + 54);
  setText(doc, [203, 213, 225], 7.5, 'normal');
  doc.text('login safety notice acknowledgement rate', x + 14, y + 68);
  setText(doc, COLORS.white, 8, 'bold');
  doc.text(`${integer(ack.acknowledged)} acknowledged  |  ${integer(ack.pending)} pending`, x + 14, y + 88);
  setText(doc, [203, 213, 225], 7.5, 'normal');
  doc.text(`Average response: ${ack.averageMinutes == null ? 'N/A' : `${decimal(ack.averageMinutes, 1)} min`}`, x + 14, y + 104);
  doc.setDrawColor(71, 99, 126);
  doc.line(x + 14, y + 116, x + width - 14, y + 116);
  setText(doc, COLORS.white, 7.4, 'normal');
  doc.text(`Qualified driver-months: ${integer(report.summary?.qualifiedDriverMonths)}`, x + 14, y + 134);
  doc.text('Supporting event and follow-up detail included', x + 14, y + 151);
}

function drawPageTitle(doc, title, subtitle) {
  doc.setFillColor(...COLORS.navy);
  doc.rect(0, 0, PAGE.width, 76, 'F');
  doc.setFillColor(...COLORS.teal);
  doc.rect(0, 0, 7, 76, 'F');
  setText(doc, COLORS.white, 17, 'bold');
  doc.text(ascii(title), PAGE.margin, 34);
  setText(doc, [190, 211, 230], 8, 'normal');
  doc.text(ascii(subtitle), PAGE.margin, 53);
}

function drawTable(doc, { title, subtitle, columns, rows, startY = 104 }) {
  let y = startY;
  const rowHeight = 21;
  const drawHeader = () => {
    doc.setFillColor(...COLORS.navySoft);
    doc.roundedRect(PAGE.margin, y, PAGE.width - (PAGE.margin * 2), 24, 4, 4, 'F');
    let x = PAGE.margin;
    columns.forEach((column) => {
      setText(doc, COLORS.white, 6.8, 'bold');
      const textX = column.align === 'right' ? x + column.width - 7 : x + 7;
      doc.text(column.label.toUpperCase(), textX, y + 15, { align: column.align || 'left' });
      x += column.width;
    });
    y += 28;
  };
  drawHeader();
  rows.forEach((row, index) => {
    if (y + rowHeight > PAGE.footerY - 18) {
      doc.addPage();
      drawPageTitle(doc, title, subtitle);
      y = startY;
      drawHeader();
    }
    if (index % 2 === 0) {
      doc.setFillColor(...COLORS.panel);
      doc.rect(PAGE.margin, y, PAGE.width - (PAGE.margin * 2), rowHeight, 'F');
    }
    let x = PAGE.margin;
    columns.forEach((column, columnIndex) => {
      const raw = row[columnIndex];
      const color = column.color ? column.color(raw, row) : COLORS.ink;
      setText(doc, color, 7.2, column.bold ? 'bold' : 'normal');
      const textX = column.align === 'right' ? x + column.width - 7 : x + 7;
      doc.text(fitText(doc, raw, column.width - 14), textX, y + 14, { align: column.align || 'left' });
      x += column.width;
    });
    y += rowHeight;
  });
  return y;
}

function ensureSpace(doc, y, needed, title, subtitle) {
  if (y + needed <= PAGE.footerY - 12) return y;
  doc.addPage();
  drawPageTitle(doc, title, subtitle);
  return 104;
}

function drawContextAndMethodology(doc, report, startY) {
  const title = 'Coverage, context, and methodology';
  const subtitle = `${monthLabel(report.period?.from)} - ${monthLabel(report.period?.to)}`;
  let y = ensureSpace(doc, startY + 12, 194, title, subtitle);
  const half = (PAGE.width - (PAGE.margin * 2) - 14) / 2;
  const indicators = report.indicators || {};
  doc.setFillColor(...COLORS.panel);
  doc.setDrawColor(...COLORS.line);
  doc.roundedRect(PAGE.margin, y, half, 160, 8, 8, 'FD');
  doc.roundedRect(PAGE.margin + half + 14, y, half, 160, 8, 8, 'FD');
  setText(doc, COLORS.ink, 10, 'bold');
  doc.text('Recorded performance indicators', PAGE.margin + 14, y + 23);
  const indicatorRows = [
    ['Qualified score', report.summary?.fleetScore == null ? 'N/A' : `${score(report.summary.fleetScore)} / 100`],
    ['Mileage coverage', indicators.coverageQuality || 'Limited'],
    ['Severe-event share', indicators.severeEventShare == null ? 'N/A' : percent(indicators.severeEventShare)],
    ['Clean-month rate', indicators.cleanMonthRate == null ? 'N/A' : percent(indicators.cleanMonthRate)],
    ['Event-rate trend', indicators.eventRateTrend || 'Insufficient history'],
    ['Notice participation', report.summary?.acknowledgements?.rate == null ? 'N/A' : percent(report.summary.acknowledgements.rate)],
  ];
  indicatorRows.forEach(([label, value], index) => {
    setText(doc, COLORS.muted, 8, 'normal');
    doc.text(label, PAGE.margin + 14, y + 45 + index * 17);
    setText(doc, COLORS.ink, 8.5, 'bold');
    doc.text(fitText(doc, value, 92), PAGE.margin + half - 14, y + 45 + index * 17, { align: 'right' });
  });
  const methodX = PAGE.margin + half + 28;
  const method = report.methodology || {};
  setText(doc, COLORS.ink, 10, 'bold');
  doc.text('How the numbers work', methodX, y + 23);
  setText(doc, COLORS.muted, 7.6, 'normal');
  const methodLines = [
    'Safety scores reflect recorded road behavior and valid mileage.',
    `A monthly score is included after at least ${decimal(method.minimumMileage, 0)} valid miles.`,
    `Event rates are shown per ${decimal(method.eventRateBasisMiles || report.summary?.eventRateBasisMiles || 100, 0)} miles, even when actual mileage is lower.`,
    'Coverage shows how much valid mileage had enough road data for complete scoring.',
  ];
  let methodY = y + 46;
  methodLines.forEach((line, index) => {
    const lines = doc.splitTextToSize(line, half - 28);
    doc.text(lines, methodX, methodY, { lineHeightFactor: 1.25 });
    methodY += Math.max(18, lines.length * 10 + (index > 2 ? 3 : 0));
  });
  y += 178;
  setText(doc, COLORS.muted, 7.2, 'normal');
  const disclosure = 'Insurance review note: This report summarizes TaxiOps operational safety records for the selected period. It supports risk review but does not replace independent underwriting analysis.';
  doc.text(doc.splitTextToSize(disclosure, PAGE.width - (PAGE.margin * 2)), PAGE.margin, y, { lineHeightFactor: 1.3 });
}

function addSafetyInsightsPage(doc, report) {
  doc.addPage();
  drawPageTitle(doc, 'Safety program overview', 'Driver participation and recorded performance, with supporting detail');
  const rows = safetyInsightRows(report.summary?.safetyInsights);
  let y = 104;
  if (!rows.length) {
    setText(doc, COLORS.muted, 10, 'normal');
    doc.text('Safety insights are not available for this report yet.', PAGE.margin, y + 16);
    y += 50;
  } else {
    const width = (PAGE.width - PAGE.margin * 2 - 14) / 2;
    rows.slice(0, 2).forEach(([label, value, detail], index) => {
      const x = PAGE.margin + index * (width + 14);
      doc.setFillColor(...COLORS.panel);
      doc.roundedRect(x, y, width, 100, 6, 6, 'F');
      setText(doc, COLORS.ink, 10, 'bold');
      doc.text(label, x + 14, y + 22);
      setText(doc, COLORS.blue, 22, 'bold');
      doc.text(value, x + 14, y + 52);
      setText(doc, COLORS.muted, 8, 'normal');
      doc.text(doc.splitTextToSize(detail, width - 28), x + 14, y + 72);
    });
    y += 132;
    setText(doc, COLORS.ink, 11, 'bold');
    doc.text('Supporting event and follow-up detail', PAGE.margin, y);
    y += 14;
    [...rows.slice(2), ['Drivers for score follow-up', integer(report.summary?.driversNeedingReview), 'Drivers with a qualified aggregate score below 70.']].forEach(([label, value, detail]) => {
      doc.setDrawColor(...COLORS.line);
      doc.line(PAGE.margin, y, PAGE.width - PAGE.margin, y);
      setText(doc, COLORS.ink, 9, 'normal');
      doc.text(label, PAGE.margin, y + 18);
      doc.text(value, PAGE.width - PAGE.margin, y + 18, { align: 'right' });
      setText(doc, COLORS.muted, 7.5, 'normal');
      doc.text(detail, PAGE.margin, y + 33);
      y += 48;
    });
  }
  setText(doc, COLORS.muted, 8, 'normal');
  doc.text(doc.splitTextToSize(SAFETY_INSIGHTS_NOTE, PAGE.width - PAGE.margin * 2), PAGE.margin, y + 16, { lineHeightFactor: 1.3 });
  const context = report.summary?.context || {};
  setText(doc, COLORS.ink, 8, 'normal');
  doc.text(`SOS alerts: ${integer(context.sos)}  |  Tracking loss: ${integer(context.trackingLoss)}  |  Inactivity cancellations: ${integer(context.staleTripCancellations)}`, PAGE.margin, y + 65);
}

function addMonthlyPages(doc, report) {
  doc.addPage();
  const title = 'Monthly performance history';
  const subtitle = 'Qualified score, exposure, coverage, and event volume';
  drawPageTitle(doc, title, subtitle);
  const columns = [
    { label: 'Month', width: 88, bold: true },
    { label: 'Score', width: 50, align: 'right', bold: true, color: (value) => riskTone(value === 'NQ' ? null : Number(value)).color },
    { label: 'Valid mi', width: 76, align: 'right' },
    { label: 'Coverage', width: 67, align: 'right' },
    { label: 'Events', width: 62, align: 'right' },
    { label: 'Qualified', width: 78, align: 'right' },
    { label: 'Below 70', width: 74, align: 'right' },
  ];
  const rows = (report.monthly || []).map((row) => [
    monthLabel(row.month),
    score(row.fleetScore),
    decimal(row.validMiles, 1),
    percent(row.scoredCoverage),
    integer(row.totalEvents),
    integer(row.qualifiedDriverMonths),
    integer(row.driversNeedingReview),
  ]);
  const endY = drawTable(doc, { title, subtitle, columns, rows, startY: 104 });
  drawContextAndMethodology(doc, report, endY);
}

function addDriverPages(doc, report) {
  doc.addPage();
  const title = report.scope?.driverId ? 'Driver safety experience' : 'Driver safety experience detail';
  const subtitle = 'Aggregate results across the selected reporting period';
  drawPageTitle(doc, title, subtitle);
  const columns = [
    { label: 'Driver', width: 128, bold: true },
    { label: 'Driver ID', width: 74 },
    { label: 'Cab', width: 38 },
    { label: 'Qual / prov', width: 66, align: 'right' },
    { label: 'Valid mi', width: 64, align: 'right' },
    { label: 'Coverage', width: 57, align: 'right' },
    { label: 'Score', width: 48, align: 'right', bold: true, color: (value) => riskTone(value === 'NQ' ? null : Number(value)).color },
    { label: 'Events', width: 53, align: 'right' },
  ];
  const rows = (report.drivers || []).map((row) => [
    ascii(row.driverName),
    ascii(row.driverId),
    ascii(row.cabNumber),
    `${integer(row.qualifiedMonths)} / ${integer(row.provisionalMonths)}`,
    decimal(row.validMiles, 1),
    percent(row.scoredCoverage),
    score(row.score),
    integer(row.totalEvents),
  ]);
  if (!rows.length) {
    setText(doc, COLORS.muted, 10, 'normal');
    doc.text('No driver safety rollups were available for the selected period.', PAGE.margin, 116);
    return;
  }
  drawTable(doc, { title, subtitle, columns, rows, startY: 104 });
}

function addFooters(doc, report) {
  const totalPages = doc.getNumberOfPages();
  for (let page = 1; page <= totalPages; page += 1) {
    doc.setPage(page);
    doc.setDrawColor(...COLORS.line);
    doc.setLineWidth(0.5);
    doc.line(PAGE.margin, PAGE.footerY, PAGE.width - PAGE.margin, PAGE.footerY);
    setText(doc, COLORS.muted, 6.8, 'normal');
    doc.text(`${ascii(report.company?.name, 'TaxiOps Fleet')}  |  Insurance Safety History  |  Confidential`, PAGE.margin, PAGE.footerY + 14);
    doc.text(`Page ${page} of ${totalPages}`, PAGE.width - PAGE.margin, PAGE.footerY + 14, { align: 'right' });
  }
}

export function insurancePdfFilename(report) {
  const scope = report.scope?.driverId ? `-${ascii(report.scope.driverId).replace(/[^A-Za-z0-9_-]/g, '-')}` : '';
  return `taxiops-safety-insurance-${report.period?.from || 'from'}-to-${report.period?.to || 'to'}${scope}.pdf`;
}

export function createSafetyInsurancePdf(report) {
  if (!report?.summary || !report?.period) throw new Error('Insurance safety report data is incomplete.');
  const doc = new jsPDF({ unit: 'pt', format: 'letter', orientation: 'portrait', compress: true });
  doc.setProperties({
    title: `${ascii(report.company?.name, 'TaxiOps Fleet')} Insurance Safety History`,
    subject: `Fleet safety report for ${report.period.from} through ${report.period.to}`,
    author: 'TaxiOps',
    creator: 'TaxiOps Fleet Safety Intelligence',
    keywords: 'fleet safety, insurance, driver safety, risk, compliance',
  });
  drawBrandHeader(doc, report);
  const summary = report.summary;
  const gap = 10;
  const metricWidth = (PAGE.width - (PAGE.margin * 2) - (gap * 3)) / 4;
  const scoreTone = riskTone(summary.fleetScore);
  drawMetric(doc, PAGE.margin, 151, metricWidth, 'Fleet score', score(summary.fleetScore), scoreTone.label, scoreTone.color);
  drawMetric(doc, PAGE.margin + metricWidth + gap, 151, metricWidth, 'Valid mileage', decimal(summary.validMiles, 1), `${percent(summary.scoredCoverage)} scored`, COLORS.green);
  drawMetric(doc, PAGE.margin + (metricWidth + gap) * 2, 151, metricWidth, 'Road events', integer(summary.totalEvents), `${decimal(summary.eventRate ?? summary.eventRatePer100Miles, 2)} / ${integer(summary.eventRateBasisMiles || 100)} mi`, COLORS.blue);
  drawMetric(doc, PAGE.margin + (metricWidth + gap) * 3, 151, metricWidth, 'Drivers', integer(summary.driverCount), 'Included in reporting period', COLORS.blue);
  drawAssessment(doc, report, PAGE.margin, 237, PAGE.width - (PAGE.margin * 2));
  drawTrendChart(doc, report, PAGE.margin, 325, PAGE.width - (PAGE.margin * 2), 181);
  const bottomWidth = (PAGE.width - (PAGE.margin * 2) - 12) / 2;
  drawEventMix(doc, report, PAGE.margin, 522, bottomWidth, 174);
  drawCompliance(doc, report, PAGE.margin + bottomWidth + 12, 522, bottomWidth, 174);
  addMonthlyPages(doc, report);
  addSafetyInsightsPage(doc, report);
  addDriverPages(doc, report);
  addFooters(doc, report);
  return doc;
}

export function downloadSafetyInsurancePdf(report) {
  const doc = createSafetyInsurancePdf(report);
  doc.save(insurancePdfFilename(report));
  return doc;
}
